import { ReactElement } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { InfographicFragment } from '@/__generated__/graphql';
import { getLanguage } from '@/lib/config';
import { colors, GridContainer, GridContainerEdge, GridWrapper, h1, h4, mediaQueries } from '@/theme/lib';
import { bicentenaryBackgroundClass } from '@components/BicentenaryPatterns/util';
import { getGridContainerEdgeStyling } from '@components/BicentenaryPatterns/BicentenaryPatterns.styles';
import { isBicentenary } from '@/lib/utils';

type StatProps = {
  statistic: string;
  description: string;
};

const StyledStat = styled.li<StatProps>`
  display: block;
  list-style: none;
  color: ${(props) => props.theme.colors.cards.infographic.color};
  text-align: center;

  flex-basis: 100%;
  padding: 0 0 3rem 0;
  ${mediaQueries.md} {
    flex-basis: 33%;
    padding: 0;
  }

  .statistic {
    ${h1}
    font-family: ${(props) => props.theme.fonts.infographicStatistic};
    font-weight: 700;
    sup {
      display: inline-block;
      vertical-align: top;
      transform: translateY(-30%);
    }
    sub {
      display: inline-block;
      vertical-align: bottom;
      transform: translateY(30%);
    }
  }
  .description {
    ${h4}
    ${(props) => isBicentenary(props.theme.key) ? css`color: ${colors.white};` : undefined};
    margin-top: 0.5rem;
    ${mediaQueries.md} {
      margin-top: 0.75rem;
    }
  }
`;

const StyledStatContainer = styled(GridWrapper)`
  display: flex;
  flex-basis: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: top;
  border-radius: ${(props) => props.theme.config.componentRadius};
  background-color: ${(props) => isBicentenary(props.theme.key) ? colors.seagrass : props.theme.colors.cards.infographic.background};
  padding: 3rem 2.25rem 0;

  ${mediaQueries.md} {
    flex-basis: 33%;
    flex-direction: row;
    justify-content: space-around;
    padding: 3.375rem 3rem 3rem;
    gap: 3rem;
  }
`;

export const InfographicGridContainerEdge = styled(GridContainerEdge)`
  padding-top: 2rem;
  padding-bottom: 2rem;
  ${mediaQueries.md} {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }
  ${(props) => getGridContainerEdgeStyling({ ...props, background: 'seagrass_with_texture' })}
`;

// Component
export function InfographicComponent(props: InfographicFragment): ReactElement<any> {
  const { statistics } = props;
  const language = getLanguage();

  return (
    <StyledStatContainer alignment={'full-width'}>
      {statistics.map((stat, idx) => (
        <StyledStat key={idx} {...stat}>
          <div
            className="statistic"
            dangerouslySetInnerHTML={{ __html: stat.statistic as string }}
            lang={language !== 'en' ? language : undefined}
          ></div>
          <div className="description" lang={language !== 'en' ? language : undefined}>
            {stat.description}
          </div>
        </StyledStat>
      ))}
    </StyledStatContainer>
  );
}

export default function Infographic(props: InfographicFragment): ReactElement {
  const theme = useTheme();
  if (isBicentenary(theme.key)) {
    return (
      <InfographicGridContainerEdge className={`${props.__typename} ${bicentenaryBackgroundClass(props)}`} key={props.id}>
        <GridContainer className={props.__typename} key={props.id}>
          <InfographicComponent {...props} />
        </GridContainer>
      </InfographicGridContainerEdge>
    );
  }
  return (
    <GridContainer className={props.__typename} key={props.id}>
      <InfographicComponent {...props} />
    </GridContainer>
  );
}
